import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";

const createEmptyItem = () => ({
  Type: "Iraab",
  Name: "",
  Title: "",
  points: 1,
  skilltype: "",
  itemcat: "",
  Iraab: { id: 1, Rationale: "", Value: "" },
  version: "v1",
});

const IraabItemDetail = () => {
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";

  const [item, setItem] = useState(createEmptyItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  //const AUTHOR_ID = "3b4f5146-b43f-4508-b136-3490cfa7dd11";

  // 🟢 Load taxonomy + item
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          isNew ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
        ]);
        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        if (itemResponse?.item) {
          const loadedItem = itemResponse.item;
          setItem({
            Type: loadedItem.Type || "Iraab",
            Name: loadedItem.Name || "",
            Title: loadedItem.Title || "",
            points: loadedItem.points ?? 1,
            skilltype: loadedItem.skilltype || "",
            itemcat: loadedItem.itemcat || "",
            Iraab: loadedItem.Iraab || { id: 1, Rationale: "", Value: "" },
            version: loadedItem.version || "v1",
          });
        }
      } catch (err) {
        console.error("Failed to load Iraab item:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [itemId, isNew]);

  // 🧩 Handlers
  const handleFieldChange = (key, value) =>
    setItem((prev) => ({ ...prev, [key]: value }));

  const handleIraabChange = (field, value) => {
    setItem((prev) => ({
      ...prev,
      Iraab: { ...prev.Iraab, [field]: value },
    }));
  };

  // 🟢 Save function
  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();
    const normalizedPoints = Number(item.points) || 1;

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: `item#${itemId || "new"}`,
      createdBy: "",
      updatedBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: `item#${itemId || "new"}`,

      // Required top-level fields
      itemcat: item.itemcat,
      skilltype: item.skilltype,

      Name: item.Name,
      Iraab: {
        id: 1,
        Rationale: item.Iraab.Rationale || "",
        Value: item.Iraab.Value || "",
      },
      points: normalizedPoints,
      Title:
        item.Title ||
        "<div>Click on the words and select the word with correct harakaat</div>",
      Type: "Iraab",
      version: "v1",
    };

    try {
      setSaving(true);
      await ItemService.saveItem(payload, isNew ? undefined : itemId);
      setSaveSuccess(true);
      setSaveError(null);
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, itemId, isNew]);

  const jsonPreview = useMemo(() => JSON.stringify(item, null, 2), [item]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading Iraab item...</Typography>
      </Paper>
    );

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="Iraab" color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {isNew ? "Create Iraab Item" : "Edit Iraab Item"}
              </Typography>
            </Stack>
            <Button variant="outlined" onClick={() => setJsonDialogOpen(true)}>
              View JSON
            </Button>
          </Stack>

          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(false)}>
              Iraab item saved successfully!
            </Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <Divider />

          {/* === Basic Fields === */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                fullWidth
                required
                value={item.Name}
                onChange={(e) => handleFieldChange("Name", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                required
                value={item.points}
                onChange={(e) => handleFieldChange("points", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  label="Skill Type"
                  onChange={(e) =>
                    handleFieldChange(
                      "skilltype",
                      normalizeSkillTypeValue(e.target.value)
                    )
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  label="Item Category"
                  onChange={(e) => handleFieldChange("itemcat", e.target.value)}
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* === Title === */}
          <Divider />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Instructions (Title)
            </Typography>
            <ReactQuill
              value={item.Title}
              onChange={(val) => handleFieldChange("Title", val)}
              placeholder="Write instructions for the learner..."
            />
          </Box>

          {/* === Iraab Content === */}
          <Divider />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Iraab Text
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={item.Iraab.Value}
              onChange={(e) => handleIraabChange("Value", e.target.value)}
              placeholder="Enter Arabic sentence(s) with words learners will select..."
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Rationale (Feedback)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={item.Iraab.Rationale}
              onChange={(e) => handleIraabChange("Rationale", e.target.value)}
              placeholder="Provide explanation or hint for this Iraab sentence..."
            />
          </Box>

          {/* === Save Button === */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Item"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* === JSON Dialog === */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default IraabItemDetail;
